import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext();

// Normalize role names to handle common variants
const normalizeRole = (r) => {
    if (!r) return '';
    const s = String(r).trim().toLowerCase();
    const map = {
        'propriétaire': 'owner',
        'proprietaire': 'owner',
        'proprietair': 'owner',
        'owner': 'owner',
        'admin': 'admin',
        'administrator': 'admin',
        'administrateur': 'admin'
    };
    return map[s] || s;
};

export const AuthProvider = ({ children }) => {
    // ── Token en mémoire uniquement (protection XSS vs localStorage) ──────────
    // La persistance de session est assurée par le refresh token httpOnly cookie.
    const [token, setToken] = useState(null);
    const [user, setUser] = useState(null);
    const [activeTenantId, setActiveTenantId] = useState(() => localStorage.getItem('activeTenantId'));
    const [refreshKey, setRefreshKey] = useState(0);
    // loading = true tant que la tentative de refresh initiale n'est pas terminée
    const [loading, setLoading] = useState(true);
    // Évite les doubles appels au refresh endpoint
    const refreshInProgress = useRef(false);
    // Évite la double initialisation due au StrictMode React (dev) qui invoque les effets 2x
    const authInitializedRef = useRef(false);
    // Flag pour distinguer un logout volontaire d'une expiration de session
    const loggingOutRef = useRef(false);

    const applyTheme = useCallback((theme, colorTheme = 'default') => {
        const html = document.documentElement;
        if (theme === 'dark') {
            html.classList.add('dark');
        } else if (theme === 'light') {
            html.classList.remove('dark');
        } else {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            prefersDark ? html.classList.add('dark') : html.classList.remove('dark');
        }
        html.classList.remove('theme-green', 'theme-blue', 'theme-red');
        if (colorTheme && colorTheme !== 'default') {
            html.classList.add(`theme-${colorTheme}`);
        }
    }, []);

    const logout = useCallback(async () => {
        loggingOutRef.current = true;
        // Effacer le refresh token cookie côté serveur
        try {
            await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
        } catch (_) { /* silencieux */ }
        localStorage.removeItem('activeTenantId');
        setToken(null);
        setUser(null);
        setActiveTenantId(null);
    }, [token]);

    // Tente de récupérer un access token via le refresh token cookie httpOnly.
    // Renvoie le nouveau token ou null si le cookie est absent/expiré.
    const tryRefresh = useCallback(async () => {
        if (refreshInProgress.current) return null;
        refreshInProgress.current = true;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
                method: 'POST',
                credentials: 'include' // envoie le cookie httpOnly
            });
            if (res.ok) {
                const data = await res.json();
                return data.token || null;
            }
            return null;
        } catch (_) {
            return null;
        } finally {
            refreshInProgress.current = false;
        }
    }, []);

    const fetchUserData = useCallback(async (accessToken) => {
        if (!accessToken) {
            setLoading(false);
            return;
        }
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });

            if (response.ok) {
                const userData = await response.json();
                // Priorité : localStorage > serveur.
                // Chaque device conserve son propre tenant actif indépendamment.
                const savedTenantId = localStorage.getItem('activeTenantId');
                const tenantId = savedTenantId || userData.activeTenantId;
                setActiveTenantId(tenantId);
                if (tenantId) localStorage.setItem('activeTenantId', tenantId);
                // Aligner user.activeTenantId sur la valeur cliente pour éviter
                // toute divergence entre l'objet user et l'état activeTenantId.
                setUser({ ...userData, activeTenantId: tenantId });
                applyTheme(userData.theme, userData.colorTheme);
            } else if (response.status === 401) {
                // Access token expiré → tenter un refresh silencieux
                const newToken = await tryRefresh();
                if (newToken) {
                    setToken(newToken);
                    await fetchUserData(newToken);
                } else {
                    logout();
                }
            } else {
                // Erreur temporaire (5xx) — garder l'utilisateur connecté
                console.warn('Erreur temporaire lors du fetch user, maintien de la session');
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        } finally {
            setLoading(false);
        }
    }, [applyTheme, logout, tryRefresh]);

    // Au montage : pas de token en mémoire → tenter le refresh via cookie.
    // Le guard authInitializedRef empêche la double-invocation de React StrictMode (dev)
    // qui causerait un flash de la page login pendant ~1s.
    useEffect(() => {
        if (authInitializedRef.current) return;
        authInitializedRef.current = true;
        (async () => {
            const newToken = await tryRefresh();
            if (newToken) {
                setToken(newToken);
                await fetchUserData(newToken);
            } else {
                setLoading(false);
            }
        })();
    // Exécuté une seule fois au montage
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = useCallback(async (newToken, userData) => {
        // Le refresh token cookie est posé par le backend (Set-Cookie httpOnly).
        // On stocke le token en mémoire et on affiche d'abord les données minimales
        // (email, name, theme) pour une UX immédiate, puis on appelle /auth/me pour
        // récupérer les tenants enrichis (plan, rôle, etc.) que le login n'envoie pas.
        setToken(newToken);
        setUser(userData);
        applyTheme(userData.theme, userData.colorTheme);
        if (userData.activeTenantId) {
            setActiveTenantId(userData.activeTenantId);
            localStorage.setItem('activeTenantId', userData.activeTenantId);
        }
        // Récupérer les données complètes avec tenants enrichis
        await fetchUserData(newToken);
    }, [applyTheme, fetchUserData]);

    const updateUser = useCallback((userData) => {
        setUser(prev => {
            const merged = { ...(prev || {}), ...(userData || {}) };
            if (!userData?.favoriteMessages && prev?.favoriteMessages) merged.favoriteMessages = prev.favoriteMessages;
            if (!userData?.collapsedMessages && prev?.collapsedMessages) merged.collapsedMessages = prev.collapsedMessages;
            if (!userData?.favoritesViewMode && prev?.favoritesViewMode) merged.favoritesViewMode = prev.favoritesViewMode;
            if (userData?.showColoredBorder === undefined && prev?.showColoredBorder !== undefined) merged.showColoredBorder = prev.showColoredBorder;
            const incomingTenantsEnriched = userData?.tenants?.some(t => t.plan !== undefined);
            if (!incomingTenantsEnriched && prev?.tenants?.some(t => t.plan !== undefined)) {
                merged.tenants = prev.tenants;
            }
            return merged;
        });
        applyTheme(userData?.theme || undefined, userData?.colorTheme || undefined);
        setRefreshKey(prev => prev + 1);
    }, [applyTheme]);

    const getAuthHeaders = useCallback(() => {
        if (!token) return {};
        const headers = { 'Authorization': `Bearer ${token}` };
        // Envoyer le tenant actif du device dans chaque requête.
        // Le backend (tenantMiddleware) lit ce header en priorité sur user.activeTenantId
        // en base, ce qui garantit que chaque device utilise son propre tenant
        // même si un autre device a fait un switchTenant entre-temps.
        if (activeTenantId) headers['x-tenant-id'] = activeTenantId;
        return headers;
    }, [token, activeTenantId]);

    const switchTenant = useCallback(async (tenantId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/tenants/switch/${tenantId}`, {
                method: 'PUT',
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || 'Erreur lors du changement d\'organisation');
            }
        } catch (err) {
            console.error('Erreur API switchTenant:', err);
            throw err;
        }
        setActiveTenantId(tenantId);
        localStorage.setItem('activeTenantId', tenantId);
        setRefreshKey(prev => prev + 1);
        await fetchUserData(token);
    }, [fetchUserData, getAuthHeaders, token]);

    const toggleFavorite = useCallback(async (messageId) => {
        if (!activeTenantId) return false;
        const isFavorite = user?.favoriteMessages?.includes(messageId);
        const method = isFavorite ? 'DELETE' : 'POST';
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/auth/preferences/favorites/${messageId}`,
                { method, headers: getAuthHeaders() }
            );
            if (response.ok) {
                const data = await response.json();
                setUser(prev => ({ ...prev, favoriteMessages: data.favoriteMessages }));
                setRefreshKey(prev => prev + 1);
                return true;
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
        }
        return false;
    }, [user, getAuthHeaders, activeTenantId]);

    const setFavoritesViewMode = useCallback(async (mode) => {
        if (!activeTenantId) return false;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/preferences/view-mode`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ favoritesViewMode: mode })
            });
            if (response.ok) {
                const data = await response.json();
                setUser(prev => ({ ...prev, favoritesViewMode: data.favoritesViewMode }));
                setRefreshKey(prev => prev + 1);
                return true;
            }
        } catch (error) {
            console.error('Error setting view mode:', error);
        }
        return false;
    }, [getAuthHeaders, activeTenantId]);

    const toggleColoredBorder = useCallback(async () => {
        if (!activeTenantId) return false;
        const newValue = !(user?.showColoredBorder ?? true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/preferences/colored-border`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ showColoredBorder: newValue })
            });
            if (response.ok) {
                setUser(prev => ({ ...prev, showColoredBorder: newValue }));
                setRefreshKey(prev => prev + 1);
                return true;
            }
        } catch (error) {
            console.error('Error toggling colored border:', error);
        }
        return false;
    }, [user, getAuthHeaders, activeTenantId]);

    const toggleCollapsed = useCallback(async (messageId) => {
        if (!activeTenantId) return false;
        const isCollapsed = user?.collapsedMessages?.includes(messageId);
        const newCollapsed = isCollapsed
            ? user.collapsedMessages.filter(id => id !== messageId)
            : [...(user.collapsedMessages || []), messageId];
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/preferences/collapsed`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ collapsedMessages: newCollapsed })
            });
            if (response.ok) {
                const data = await response.json();
                setUser(prev => ({ ...prev, collapsedMessages: data.collapsedMessages }));
                return true;
            }
        } catch (error) {
            console.error('Error toggling collapsed:', error);
        }
        return false;
    }, [user, getAuthHeaders, activeTenantId]);

    const hasTenantRole = useCallback((tenantId, roles = []) => {
        if (!user) return false;
        const wanted = (roles || []).map(r => String(r).toLowerCase());
        if (user.tenantRoles && tenantId && user.tenantRoles[tenantId]) {
            const roleValue = normalizeRole(user.tenantRoles[tenantId]);
            if (wanted.includes(roleValue)) return true;
        }
        if (Array.isArray(user.tenants) && tenantId) {
            const t = user.tenants.find(tt => tt._id === tenantId || tt.id === tenantId || tt.tenantId === tenantId);
            if (t) {
                const candidate = normalizeRole(t.userRole || t.role || t.roleName || t.roleLabel || t.role_type || t.roleType);
                if (candidate && wanted.includes(candidate)) return true;
            }
        }
        if (user.role) {
            const r = normalizeRole(user.role);
            if (wanted.includes(r)) return true;
        }
        if (Array.isArray(user.roles) && user.roles.length) {
            for (const rr of user.roles) {
                const r = normalizeRole(rr);
                if (wanted.includes(r)) return true;
            }
        }
        return false;
    }, [user]);

    const refreshUser = useCallback(() => fetchUserData(token), [fetchUserData, token]);

    return (
        <AuthContext.Provider value={{
            user,
            token,
            activeTenantId,
            loading,
            login,
            logout,
            loggingOut: loggingOutRef,
            updateUser,
            getAuthHeaders,
            switchTenant,
            toggleFavorite,
            setFavoritesViewMode,
            toggleColoredBorder,
            toggleCollapsed,
            refreshKey,
            hasTenantRole,
            refreshUser
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
